import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, Upload, FileSpreadsheet, TrendingUp } from 'lucide-react';

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-700 to-teal-500 rounded-xl flex items-center justify-center">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold gradient-text">Government Window Pricing Tool</h1>
          </div>
          <div className="flex space-x-4">
            <button 
              onClick={() => navigate('/login')}
              className="btn-secondary"
              data-testid="login-nav-btn"
            >
              Sign In
            </button>
            <button 
              onClick={() => navigate('/login')}
              className="btn-primary"
              data-testid="get-started-nav-btn"
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium">
                <FileSpreadsheet className="w-4 h-4" />
                <span>Excel-Powered Pricing Engine</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight" style={{letterSpacing: '-0.02em'}}>
                Transform Your
                <span className="gradient-text block mt-2">Pricing Logic</span>
              </h1>
              
              <p className="text-xl text-slate-600 leading-relaxed">
                Access our professional pricing calculator powered by pre-configured Excel logic with 2313 formulas. 
                Real-time calculations, comprehensive analysis, and seamless exports—ready to use immediately.
              </p>
              
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <button 
                  onClick={() => navigate('/calculator')}
                  className="btn-primary inline-flex items-center justify-center space-x-2"
                  data-testid="get-started-calculator-btn"
                >
                  <Calculator className="w-5 h-5" />
                  <span>Start Calculating</span>
                </button>
                <button 
                  onClick={() => navigate('/login')}
                  className="btn-secondary inline-flex items-center justify-center space-x-2"
                  data-testid="sign-in-nav-btn"
                >
                  <span>Sign In</span>
                </button>
              </div>
            </div>

            <div className="relative animate-slide-up">
              <div className="relative z-10 glass-card p-8 rounded-2xl shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1763259012676-6780139b4aad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGdsYXNzJTIwc2hhcGVzJTIwM2R8ZW58MHx8fHwxNzcwNDE2NzUzfDA&ixlib=rb-4.1.0&q=85"
                  alt="Abstract geometric shapes"
                  className="w-full h-auto rounded-xl"
                />
              </div>
              <div className="absolute -z-10 top-8 right-8 w-full h-full bg-gradient-to-br from-blue-200 to-teal-200 rounded-2xl blur-3xl opacity-30"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Powerful Features</h2>
            <p className="text-xl text-slate-600">Everything you need to manage Excel-powered pricing</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <FileSpreadsheet className="w-8 h-8" />,
                title: "Fixed Excel Logic",
                description: "Pre-configured calculation engine with 2313 formulas from FeeTable_Analytics sheet"
              },
              {
                icon: <Calculator className="w-8 h-8" />,
                title: "Real-Time Calculation",
                description: "Instant recalculation as you change inputs, with full breakdown of intermediate steps"
              },
              {
                icon: <TrendingUp className="w-8 h-8" />,
                title: "Comparison View",
                description: "Side-by-side comparison of incumbent vs proposed pricing with detailed metrics"
              },
              {
                icon: <FileSpreadsheet className="w-8 h-8" />,
                title: "Export Options",
                description: "Export results to PDF and CSV with professional formatting and full details"
              },
              {
                icon: <Calculator className="w-8 h-8" />,
                title: "Calculation History",
                description: "Track all pricing calculations with timestamps and full input/output details"
              },
              {
                icon: <TrendingUp className="w-8 h-8" />,
                title: "Professional Results",
                description: "Comprehensive pricing analysis with net revenue, margins, and annual impact"
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="card p-8 hover:border-blue-200 cursor-pointer group"
                style={{animationDelay: `${index * 0.1}s`}}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-teal-100 rounded-2xl flex items-center justify-center text-blue-700 mb-6 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-blue-700 to-blue-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Pricing?
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Access the calculator now or view your calculation history
          </p>
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center">
            <button 
              onClick={() => navigate('/calculator')}
              className="bg-white text-blue-700 hover:bg-blue-50 px-8 py-4 rounded-full font-medium text-lg shadow-lg transition-all hover:-translate-y-1"
              data-testid="cta-calculator-btn"
            >
              Go to Calculator
            </button>
            <button 
              onClick={() => navigate('/admin')}
              className="bg-teal-500 text-white hover:bg-teal-400 px-8 py-4 rounded-full font-medium text-lg shadow-lg transition-all hover:-translate-y-1"
              data-testid="cta-admin-btn"
            >
              View History
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm">© 2026 Government Window Pricing Tool. Built with Excel-powered intelligence.</p>
        </div>
      </footer>
    </div>
  );
}
